import { Combobox, Option, Tag, TagGroup } from "@fluentui/react-components";
import React, { useContext, useState } from "react";
import { ColumnOptionsContext } from "./ColumnOptions";
import { Column, TaxonomyFilter as TaxonomyFilterType } from "./types";
import styled from "@emotion/styled";

type Props = {
  column: Column;
};

const TagGroupWrapper = styled.div`
  margin-bottom: 8px;
`;

export const TaxonomySelectFilter = ({ column }: Props) => {
  const filter = column.filter as TaxonomyFilterType;
  const { taxonomy, setFilter, t, id } = useContext(ColumnOptionsContext);
  const [query, setQuery] = useState("");

  const allOptions = taxonomy[filter.taxonomyKey]
    .filter((tax) => {
      if (filter.exclude) {
        return !filter.exclude.includes(tax.code);
      }
      return true;
    })
    .map((tax) => ({
      name: filter.customOptionLabel
        ? filter.customOptionLabel(tax)
        : t(tax.code),
      id: tax.id,
      code: tax.code,
    }));

  const filteredOptions = allOptions.filter((option) => {
    const matchesQuery =
      query === "" || option.name.toLowerCase().includes(query.toLowerCase());
    const notSelected = !filter.value.includes(option.id);
    return matchesQuery && notSelected;
  });

  const selectedItems = filter.value
    .map((id) => allOptions.find((opt) => opt.id === id))
    .filter(Boolean) as typeof allOptions;

  const handleRemoveItem = (itemId: number) => {
    setFilter(column.key, {
      ...filter,
      value: filter.value.filter((id) => id !== itemId),
    });
  };

  const handleSelectOption = (optionId: number) => {
    setFilter(column.key, {
      ...filter,
      value: [...filter.value, optionId],
    });
    setQuery("");
  };

  return (
    <div>
      {selectedItems.length > 0 && (
        <TagGroupWrapper>
          <TagGroup>
            {selectedItems.map((item) => (
              <Tag
                key={item.id}
                dismissible
                dismissIcon={{ onClick: () => handleRemoveItem(item.id) }}
              >
                {item.name}
              </Tag>
            ))}
          </TagGroup>
        </TagGroupWrapper>
      )}
      <Combobox
        placeholder={t(column.placeholder)}
        id={id ? `${id}-taxonomySelect-input-${column.key}` : undefined}
        value={query}
        onInput={(e) => setQuery(e.currentTarget.value)}
        onOptionSelect={(_, data) => {
          const selectedId = Number(data.optionValue);
          if (selectedId) {
            handleSelectOption(selectedId);
          }
        }}
      >
        {filteredOptions.map((option) => (
          <Option key={option.id} value={option.id.toString()}>
            {option.name}
          </Option>
        ))}
      </Combobox>
    </div>
  );
};
